/**
 * Configuración centralizada para tipos de documentos DTE
 * Este archivo define la estructura, validaciones y comportamiento de cada tipo de documento
 */

// ============================================
// TIPOS DE DOCUMENTOS DTE
// ============================================

/**
 * Tipos de documentos DTE disponibles
 * Cada tipo define:
 * - name: Nombre para mostrar
 * - schema: Archivo de esquema JSON relacionado
 * - requiresReceptor: Si requiere datos del receptor
 * - requiresNRC: Si requiere NRC (puede ser boolean o función)
 * - hasDetailItems: Si tiene items/detalle de productos
 * - steps: Array de pasos requeridos para este tipo
 */
export const DTE_TYPES = [
  { 
    code: '01', 
    name: 'Factura Electrónica', 
    shortName: 'FE',
    schema: 'fe-f-v2.json',
    requiresReceptor: true,
    requiresNRC: (receptor) => receptor?.tipoDocumento === '36',
    hasDetailItems: true,
    steps: ['selection', 'receptor', 'detail', 'summary']
  },
  { 
    code: '03', 
    name: 'Comprobante de Crédito Fiscal Electrónico', 
    shortName: 'CCFE',
    schema: 'fe-ccf-v4.json',
    requiresReceptor: true,
    requiresNRC: true,
    hasDetailItems: true,
    steps: ['selection', 'receptor', 'detail', 'summary']
  },
  { 
    code: '04', 
    name: 'Nota de Remisión Electrónica', 
    shortName: 'NRE',
    schema: 'fe-nr-v4.json',
    requiresReceptor: true,
    requiresNRC: false,
    hasDetailItems: true,
    steps: ['selection', 'receptor', 'detail', 'summary']
  },
  { 
    code: '05', 
    name: 'Nota de Crédito Electrónica', 
    shortName: 'NCE',
    schema: 'fe-nc-v4.json',
    requiresReceptor: true,
    requiresNRC: (receptor) => receptor?.tipoDocumento === '36',
    hasDetailItems: true,
    steps: ['selection', 'receptor', 'detail', 'summary']
  },
  { 
    code: '06', 
    name: 'Nota de Débito Electrónica', 
    shortName: 'NDE',
    schema: 'fe-nd-v4.json',
    requiresReceptor: true,
    requiresNRC: (receptor) => receptor?.tipoDocumento === '36',
    hasDetailItems: true,
    steps: ['selection', 'receptor', 'detail', 'summary']
  },
  { 
    code: '07', 
    name: 'Comprobante de Retención Electrónico', 
    shortName: 'CRE',
    schema: 'fe-cr-v2.json',
    requiresReceptor: true,
    requiresNRC: true,
    hasDetailItems: true,
    hasDocumentReference: true, // CR referencia otros documentos (FE, CCF, NRE)
    steps: ['selection', 'receptor', 'detail', 'summary']
  },
  { 
    code: '08', 
    name: 'Comprobante de Liquidación Electrónico', 
    shortName: 'CLE',
    schema: 'fe-cl-v2.json',
    requiresReceptor: true,
    requiresNRC: true,
    hasDetailItems: true,
    steps: ['selection', 'receptor', 'detail', 'summary']
  },
  { 
    code: '09', 
    name: 'Documento Contable de Liquidación Electrónico', 
    shortName: 'DCLE',
    schema: 'fe-dcl-v2.json',
    requiresReceptor: true,
    requiresNRC: true,
    hasDetailItems: true,
    steps: ['selection', 'receptor', 'detail', 'summary']
  },
  { 
    code: '11', 
    name: 'Factura de Exportación Electrónica', 
    shortName: 'FEXE',
    schema: 'fe-fex-v3.json',
    requiresReceptor: true,
    requiresNRC: false,
    hasDetailItems: true,
    steps: ['selection', 'receptor', 'detail', 'summary']
  },
  { 
    code: '14', 
    name: 'Factura de Sujeto Excluido Electrónica', 
    shortName: 'FSEE',
    schema: 'fe-fse-v2.json',
    requiresReceptor: true,
    requiresNRC: false,
    hasDetailItems: true,
    steps: ['selection', 'receptor', 'detail', 'summary']
  },
  { 
    code: '15', 
    name: 'Comprobante de Donación Electrónico', 
    shortName: 'CDE',
    schema: 'fe-cd-v2.json',
    requiresReceptor: true,
    requiresNRC: false,
    hasDetailItems: true,
    steps: ['selection', 'receptor', 'detail', 'summary']
  },
  // NOTA: La anulación (código '15') se maneja por separado en DTEAnulacionWizard
  // No se incluye aquí para evitar que aparezca en el menú de creación de nuevos DTE
];

// ============================================
// TIPOS DE DOCUMENTOS DE IDENTIFICACIÓN
// ============================================

/**
 * Catálogo de tipos de documentos de identificación (CAT-22)
 */
export const DOCUMENT_TYPES = [
  { code: '36', name: 'NIT', pattern: /^\d{4}-\d{6}-\d{3}-\d$/, maxLength: 14, format: '####-######-###-#' },
  { code: '13', name: 'DUI', pattern: /^\d{8}-\d$/, maxLength: 10, format: '########-#' },
  { code: '02', name: 'Carnet Residente', pattern: /^[A-Z]\d{6,7}$/, maxLength: 8, format: 'Letra + 6-7 dígitos' },
  { code: '03', name: 'Pasaporte', pattern: /^[A-Z][0-9]{6,8}$/, maxLength: 9, format: 'Letra + 6-8 dígitos' },
  { code: '37', name: 'Otro', pattern: /^[\w-]{3,20}$/, maxLength: 20, format: '3-20 caracteres' },
];

// ============================================
// DOCUMENTOS QUE SE PUEDEN ANULAR
// ============================================

/**
 * Tipos de documentos que pueden ser anulados
 */
export const ANULABLE_DOCUMENT_TYPES = [
  { code: '01', name: 'Factura Electrónica' },
  { code: '03', name: 'Comprobante de Crédito Fiscal' },
  { code: '04', name: 'Nota de Remisión' },
  { code: '05', name: 'Nota de Crédito' },
  { code: '06', name: 'Nota de Débito' },
  { code: '07', name: 'Comprobante de Retención' },
  { code: '08', name: 'Comprobante de Liquidación' },
  { code: '09', name: 'Documento Contable de Liquidación' },
  { code: '11', name: 'Factura de Exportación' },
  { code: '14', name: 'Factura de Sujeto Excluido' },
  { code: '15', name: 'Comprobante de Donación' },
];

// ============================================
// PAÍSES PARA WHATSAPP
// ============================================

export const COUNTRIES = [
  { code: 'SV', name: 'El Salvador', prefix: '503' },
  { code: 'GT', name: 'Guatemala', prefix: '502' },
  { code: 'CR', name: 'Costa Rica', prefix: '506' },
  { code: 'HN', name: 'Honduras', prefix: '504' },
  { code: 'PA', name: 'Panamá', prefix: '507' },
];

// ============================================
// MÁSCARAS DE FORMATEO
// ============================================

/**
 * Funciones de formato para cada tipo de documento
 */
export const DOCUMENT_FORMATTERS = {
  '36': (value) => {
    // NIT: ####-######-###-#
    const digits = value.replace(/[^0-9]/g, '').slice(0, 14);
    return digits.replace(/(\d{4})(\d{0,6})(\d{0,3})(\d{0,1})/, (match, g1, g2, g3, g4) => {
      let result = g1;
      if (g2) result += '-' + g2;
      if (g3) result += '-' + g3;
      if (g4) result += '-' + g4;
      return result;
    });
  },
  '13': (value) => {
    // DUI: ########-# (8 dígitos + guión + 1 dígito = 10 caracteres)
    const digits = value.replace(/[^0-9]/g, '').slice(0, 9);
    // Solo formatear si hay al menos 8 dígitos
    if (digits.length >= 8) {
      return digits.replace(/(\d{0,8})(\d{1})/, '$1-$2');
    }
    return digits;
  },
  '02': (value) => {
    // Carnet Residente: letra + números
    return value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 8);
  },
  '03': (value) => {
    // Pasaporte: letra + números
    return value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 9);
  },
  '37': (value) => {
    // Otro:允许 letras, números y guiones
    return value.replace(/[^A-Za-z0-9-]/g, '').toUpperCase().slice(0, 20);
  },
};

// ============================================
// LONGITUDES MÁXIMAS
// ============================================

export const DOCUMENT_MAX_LENGTHS = {
  '36': 17, // NIT: 14 dígitos + 3 guiones = 17 caracteres
  '13': 10,  // DUI: 8 dígitos + guión + 1 dígito = 10
  '02': 8,  // Carnet Residente
  '03': 9,  // Pasaporte
  '37': 20, // Otro
};

// ============================================
// PASOS DEL WIZARD
// ============================================

export const STEPS_CONFIG = {
  selection: {
    key: 'selection',
    label: 'Seleccionar Tipo',
    shortLabel: 'Tipo',
    icon: 'ReceiptLong',
  },
  receptor: {
    key: 'receptor',
    label: 'Datos del Receptor',
    shortLabel: 'Receptor',
    icon: 'Person',
  },
  detail: {
    key: 'detail',
    label: 'Detalle del Documento',
    shortLabel: 'Detalle',
    icon: 'Assignment',
  },
  summary: {
    key: 'summary',
    label: 'Resumen y Envío',
    shortLabel: 'Resumen',
    icon: 'CheckCircle',
  },
  anulacion: {
    key: 'anulacion',
    label: 'Anulación de Documento',
    shortLabel: 'Anulación',
    icon: 'Cancel',
  },
};

// ============================================
// CATÁLOGO DE UNIDADES DE MEDIDA
// ============================================

export const UNIDADES_MEDIDA = [
  { code: '13', name: 'Unidad' },
  { code: '60', name: 'Kilogramo' },
  { code: '61', name: 'Gramo' },
  { code: '62', name: 'Metro' },
  { code: '63', name: 'Centímetro' },
  { code: '64', name: 'Litro' },
  { code: '65', name: 'Galón' },
  { code: '66', name: 'Caja' },
  { code: '67', name: 'Paquete' },
  { code: '68', name: 'Resma' },
  { code: '69', name: 'Bulto' },
  { code: '70', name: 'Barril' },
  { code: '71', name: 'Botella' },
  { code: '72', name: 'Lata' },
  { code: '73', name: 'Tonelada' },
  { code: '99', name: 'Servicio' },
];

// ============================================
// TIPOS DE VENTA
// ============================================

export const TIPOS_VENTA = [
  { code: 'GRAVADA', name: 'Gravada' },
  { code: 'EXENTA', name: 'Exenta' },
  { code: 'NO_SUJETA', name: 'No Sujeta' },
];

// ============================================
// TIPOS DE ITEM
// ============================================

export const TIPOS_ITEM = [
  { code: 1, name: 'Bien' },
  { code: 2, name: 'Servicio' },
  { code: 3, name: 'Ambos' },
];

// ============================================
// PLAZOS DE CRÉDITO
// ============================================

export const PLAZOS_CREDITO = [
  { code: 'dias', name: 'Días' },
  { code: 'meses', name: 'Meses' },
  { code: 'anos', name: 'Años' },
];

// ============================================
// CÓDIGOS DE RETENCIÓN MH (para CR)
// ============================================

export const CODIGOS_RETENCION_MH = [
  { code: '22', name: '22 - Rentas del trabajo' },
  { code: 'C4', name: 'C4 - Arrendamientos' },
  { code: 'C9', name: 'C9 - Otras retenciones' },
];

const roundRetentionMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

export const calculateRetentionAmounts = (
  monto,
  codigoRetencionMH,
  porcentajeEspecial = 0,
  { montoIncluyeIva = false } = {},
) => {
  const montoIngresado = Number(monto) || 0;
  const codigo = String(codigoRetencionMH || '');
  // Solo el código 22 parte del total de una FE con IVA incluido.
  // Los montos de C4 y C9 ya representan la base sujeta a retención.
  const montoSujetoGrav = roundRetentionMoney(
    codigo === '22' && montoIncluyeIva ? montoIngresado / 1.13 : montoIngresado,
  );
  const porcentaje = codigo === '22'
    ? 1
    : codigo === 'C4'
      ? 13
      : codigo === 'C9'
        ? Number(porcentajeEspecial) || 0
        : 0;
  const ivaRetenido = roundRetentionMoney(montoSujetoGrav * (porcentaje / 100));

  return {
    montoSujetoGrav,
    ivaRetenido,
    totalIva: codigo === 'C4' ? ivaRetenido : 0,
  };
};

// ============================================
// TIPOS DE DOCUMENTOS RELACIONADOS (para CR)
// ============================================

export const TIPOS_DOCUMENTO_RELACIONADO = [
  { code: '01', name: 'Factura Electrónica (FE)' },
  { code: '03', name: 'Comprobante de Crédito Fiscal (CCF)' },
  { code: '14', name: 'Factura de Sujeto Excluido (FSE)' },
];

// ============================================
// TIPOS DE GENERACIÓN (para CR)
// ============================================

export const TIPOS_GENERACION = [
  { code: 1, name: '1 - Interno' },
  { code: 2, name: '2 - Electrónico' },
];

// ============================================
// MÉTODOS DE PAGO
// ============================================

export const METODOS_PAGO = [
  { code: '01', name: 'Efectivo' },
  { code: '02', name: 'Cheque' },
  { code: '03', name: 'Tarjeta' },
  { code: '04', name: 'Transferencia' },
];

// ============================================
// FORMAS DE PAGO
// ============================================

export const FORMAS_PAGO = [
  { code: 'contado', name: 'Contado' },
  { code: 'credito', name: 'Crédito' },
];

// ============================================
// CONFIGURACIÓN DE ESTADO INICIAL
// ============================================

/**
 * Estado inicial del documento según el tipo de DTE
 */
export const getInitialDocumentState = (tipoDte = null) => {
  const schemaVersions = {
    '01': 2, '03': 4, '04': 4, '05': 4, '06': 4,
    '07': 2, '08': 2, '09': 2, '11': 3, '14': 2, '15': 2,
  };
  const baseState = {
    identificacion: {
      version: schemaVersions[tipoDte] || 1,
      ambiente: '00', // Prueba
      tipoDte: tipoDte || '',
      numeroControl: '',
      codigoGeneracion: '',
      tipoModelo: 1,
      tipoOperacion: 1,
      tipoContingencia: null,
      motivoContin: null,
      fecEmi: '',
      horEmi: '',
      tipoMoneda: 'USD',
    },
    // Nota de Crédito / Débito: debe partir de un documento emitido previamente (documentoRelacionado).
    documentoRelacionadoBase: null,
    // Sección Header - valores por defecto
	    header: {
	      fechaEmision: new Date().toISOString().split('T')[0], // Hoy por defecto
	      formaPago: 'contado', // Contado por defecto
	      plazo: 'dias', // Días por defecto
	      cantidadPlazo: 30, // 30 por defecto
	      metodoPago: '01', // Efectivo por defecto
	      sucursal: '0', // Matriz por defecto
        puntoVenta: '',
        posId: null,
	    },
    emisor: {
      nit: '',
      nrc: '',
      nombre: '',
      codActividad: '',
      descActividad: '',
      nombreComercial: '',
      tipoEstablecimiento: '01',
      direccion: {
        departamento: '',
        municipio: '',
        distrito: '',
        complemento: '',
      },
      telefono: '',
      correo: '',
      codEstableMH: '',
      codEstable: '',
      codPuntoVentaMH: '',
      codPuntoVenta: '',
    },
    receptor: null,
    cuerpoDocumento: [],
    resumen: {},
  };

  return baseState;
};

/**
 * Crea un nuevo item para el cuerpo del documento
 * Con estructura completa según schema DTE
 * @param {number} numItem - Número de item
 * @param {string} tipoDte - Tipo de documento DTE (opcional)
 */
export const createNewItem = (numItem, tipoDte = null) => {
  // Si es CR (Comprobante de Retención), crear estructura de referencia
  if (tipoDte === '07') {
    return {
      numItem,
      // Campos específicos de CR (referencia a otro documento)
      tipoDte: '01', // Por defecto: Factura Electrónica
      tipoGeneracion: 1, // Por defecto: 1 = Interno
      numeroDocumento: '', // Número de documento relacionado
      fechaEmision: new Date().toISOString().split('T')[0], // Fecha actual
      montoFacturaOriginal: null, // Total con IVA incluido; solo se usa en pantalla para código 22
      montoSujetoGrav: 0, // Monto sujeto a retención
      codigoRetencionMH: '22', // Por defecto: 22 (Rentas laborales)
      porcentajeEspecial: null, // Solo aplica para C9; se expresa como porcentaje
      ivaRetenido: 0, // IVA retenido
      descripcion: '', // Descripción de la retención
    };
  }

  // Estructura normal para otros tipos de documentos
  return {
    numItem,
    tipoItem: 1, // Bien por defecto (1=Bien, 2=Servicio, 3=Ambos, 4=Otro)
    numeroDocumento: null, // Número de documento relacionado
    codigo: '', // Código del producto
    codTributo: null, // Código de tributo (para servicios)
    descripcion: '',
    cantidad: 1,
    uniMedida: 13, // Unidad por defecto
    precioUni: 0, // Precio sin IVA
    montoDescu: 0, // Descuento por ítem
    ventaNoSuj: 0,
    ventaExenta: 0,
    ventaGravada: 0,
    tributos: null, // Array de códigos de tributo o null
    psv: 0, // Precio sugerido de venta
    noGravado: 0, // Cargos/Abonos que no afectan la base imponible
    tipoVenta: 'GRAVADA', // Gravada por defecto
    descuento: 0, // Porcentaje de descuento
    precioConIva: 0, // Precio con IVA calculado (para UI)
  };
};

// ============================================
// CONFIGURACIÓN DE CAMPOS DE RECEPTOR
// ============================================

/**
 * Define qué campos del receptor son requeridos según el tipo de DTE
 * Según los esquemas JSON de MH:
 * - CCF (03), CR (07), CLE (08), DCLE (09): requieren NRC, correo, dirección completa
 * - FE (01), NRE (04), NC (05), ND (06), FEX (11), FSE (14), CDO (15): solo datos básicos
 */
export const RECEPTOR_REQUIRED_FIELDS = {
  // Factura Electrónica (01): Solo requiere datos básicos
  '01': [
    'tipoDocumento',
    'numDocumento',
    'nombre'
  ],
  
  // CCF (03): Requiere NRC, correo, actividad económica y dirección
  '03': [
    'tipoDocumento',
    'numDocumento',
    'nombre',
    'nrc',
    'correoNotificacion',
    'codActividad',
    'departamento',
    'municipio',
    'direccion'
  ],
  
  // Nota de Remisión (04): Solo datos básicos
  '04': [
    'tipoDocumento',
    'numDocumento',
    'nombre'
  ],
  
  // Nota de Crédito (05): Solo datos básicos
  '05': [
    'tipoDocumento',
    'numDocumento',
    'nombre'
  ],
  
  // Nota de Débito (06): Solo datos básicos
  '06': [
    'tipoDocumento',
    'numDocumento',
    'nombre'
  ],
  
  // Comprobante de Retención (07): Requiere NRC, correo, actividad económica y dirección
  '07': [
    'tipoDocumento',
    'numDocumento',
    'nombre',
    'nrc',
    'correoNotificacion',
    'codActividad',
    'departamento',
    'municipio',
    'direccion'
  ],
  
  // Comprobante de Liquidación (08): Requiere NRC, correo, actividad económica y dirección
  '08': [
    'tipoDocumento',
    'numDocumento',
    'nombre',
    'nrc',
    'correoNotificacion',
    'codActividad',
    'departamento',
    'municipio',
    'direccion'
  ],
  
  // Documento Contable de Liquidación (09): Requiere NRC, correo, actividad económica y dirección
  '09': [
    'tipoDocumento',
    'numDocumento',
    'nombre',
    'nrc',
    'correoNotificacion',
    'codActividad',
    'departamento',
    'municipio',
    'direccion'
  ],
  
  // Factura de Exportación (11): Solo datos básicos
  '11': [
    'tipoDocumento',
    'numDocumento',
    'nombre'
  ],
  
  // Sujeto Excluido (14): Solo datos básicos (estructura diferente)
  '14': [
    'tipoDocumento',
    'numDocumento',
    'nombre'
  ],
  
  // Comprobante de Donación (15): Solo datos básicos
  '15': [
    'tipoDocumento',
    'numDocumento',
    'nombre'
  ]
};

/**
 * Obtiene los campos requeridos para un tipo de DTE específico
 */
export const getReceptorRequiredFields = (tipoDte) => {
  return RECEPTOR_REQUIRED_FIELDS[tipoDte] || RECEPTOR_REQUIRED_FIELDS['01'];
};

/**
 * Define qué campos del receptor son requeridos según el tipo de documento (legacy)
 */
export const RECEPTOR_FIELD_CONFIG = {
  // Campos siempre requeridos
  required: ['tipoDocumento', 'numDocumento', 'nombre'],
  
  // NRC requerido según tipo de documento
  nrc: {
    '01': (receptor) => receptor?.tipoDocumento === '36', // Solo si es NIT
    '03': () => true, // Siempre requerido para CCF
    '07': () => true, // Siempre requerido para CR
    '14': () => false, // No requerido para Sujeto Excluido
  },
  
  // Campos de contacto (al menos uno requerido)
  contacto: {
    required: false, // No requerido, pero al menos uno de los dos
    fields: ['correoNotificacion', 'whatsapp'],
  },
};

// ============================================
// AYUDANTES
// ============================================

/**
 * Obtiene la configuración de un tipo de documento por su código
 */
export const getDTETypeConfig = (code) => {
  return DTE_TYPES.find((d) => d.code === code) || null;
};

/**
 * Obtiene la configuración de un tipo de documento de identificación
 */
export const getDocumentTypeConfig = (code) => {
  return DOCUMENT_TYPES.find((d) => d.code === code) || null;
};

/**
 * Obtiene el formato de ayuda para un tipo de documento
 */
export const getDocumentHelperText = (tipoDoc) => {
  const config = getDocumentTypeConfig(tipoDoc);
  return config ? config.format : 'Número de documento';
};

/**
 * Determina si un campo de NRC es requerido según el tipo de documento
 */
export const isNRCRequired = (tipoDte, receptor) => {
  const dteConfig = getDTETypeConfig(tipoDte);
  if (!dteConfig) return false;
  
  if (typeof dteConfig.requiresNRC === 'function') {
    return dteConfig.requiresNRC(receptor);
  }
  
  return dteConfig.requiresNRC || false;
};

/**
 * Obtiene los pasos activos para un tipo de documento
 */
export const getStepsForDTE = (tipoDte) => {
  const dteConfig = getDTETypeConfig(tipoDte);
  return dteConfig ? dteConfig.steps : ['selection', 'receptor', 'detail', 'summary'];
};

/**
 * Determina si un tipo de documento es una anulación
 * Las anulaciones solo requieren tipo de documento y número de control
 */
export const isAnulacionType = (tipoDte) => {
  const dteConfig = getDTETypeConfig(tipoDte);
  return dteConfig ? dteConfig.isAnulacion === true : false;
};

/**
 * Obtiene los campos requeridos para un tipo de documento
 */
export const getRequiredFields = (tipoDte) => {
  const dteConfig = getDTETypeConfig(tipoDte);
  return dteConfig?.requiredFields || [];
};

/**
 * Determina si un tipo de documento tiene referencia a otros documentos
 * (como CR que referencia FE, CCF, NRE)
 */
export const hasDocumentReference = (tipoDte) => {
  const dteConfig = getDTETypeConfig(tipoDte);
  return dteConfig?.hasDocumentReference === true;
};
